import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RoutePoint } from "../../utils/routePoints";

/** Mirrors AutoRouteOrderRow from CreateRouteAutomatically — kept serializable for persist */
export interface RouteOptimizationOrderRow {
  id: string;
  srNo: number;
  accountNumber: string;
  customerName: string;
  customerAddress: string;
  route: string;
  stop: number;
  deliveryDay: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalBalance: number;
  futureDelivery: boolean;
  hasCustomerLocation: boolean;
  customerNumber?: number;
  customerForLocation?: {
    C_Number: number;
    C_Name?: string;
    C_Address?: string;
    C_City?: string;
    C_State?: string;
    C_Zip?: string;
    C_Phone?: string;
    C_PhoneMobile?: string;
  } | null;
  /** From order API `customerLocation` — used for map / getDeliverRouteByGoogleMap */
  customerLat?: number | null;
  customerLng?: number | null;
  /** From `Invoice_Total` — sent as `invoiceAmount` on create route stops */
  invoiceAmount?: number | null;
  /** Until API returns URLs, use `STATIC_ROUTE_ORDER_INVOICE_URL` from driverManagementApis */
  invoiceUrl?: string | null;
  /** Order_Type from API — 6 means return order */
  orderType?: number | null;
  /** Removed from this route back to Create route ("Returned from R-…" tag). */
  returnedFromRouteNumber?: string | null;
  /** Why it came back: cancelled | rejected | removed (colour of the tag). */
  returnedKind?: string | null;
  /** Being transferred from this route into the new one ("Transfer from R-…" tag). */
  transferFromRouteNumber?: string | null;
  /** Source stop id for a transfer row. */
  transferStopId?: number | null;
}

/** Persisted result from POST getDeliverRouteByGoogleMap — survives page refresh */
export interface GoogleMapRoutePersist {
  polyline: string;
  totalDistanceKm: number;
  lastStopToDestinationKm: number;
  totalDistanceMiles?: number;
  lastStopToDestinationMiles?: number;
}

export interface GoogleMapStopPersist {
  stopSequence: number;
  C_Number: number;
  orderNumbers: number;
  lat: number;
  lng: number;
  distanceKm: number;
  cumulativeDistanceKm: number;
  /** From API — display miles in UI */
  distanceMiles?: number;
  cumulativeDistanceMiles?: number;
}

/** Serialized vehicle row when user assigns — used for UI even if list API ids differ or list refetches. */
export interface AssignedVehicleSnapshot {
  id: string;
  label: string;
  description?: string;
  vinNumber?: string;
  loadCapacityLbs?: number;
  mileageHours?: string | number;
}

export interface DriverVehicleAssignment {
  driverId: string;
  driverName: string;
  truckId: string;
  truckLabel: string;
  split?: number;
  /** Populated when user picks a vehicle; keeps full specs for expanded driver card. */
  vehicleDetail?: AssignedVehicleSnapshot | null;
}

export interface MultiDriverPreviewRoute {
  driverId: number;
  truckId: number;
  day: string;
  totalStops: number;
  totalKilometers: number;
  totalMiles: number;
  totalDurationInMinutes: number;
  polyline: string;
  stops: Array<{
    stopSequence: number;
    orderNumber: number;
    C_Number: number;
    latitude: number;
    longitude: number;
    startLatitude: number;
    startLongitude: number;
    endLatitude: number;
    endLongitude: number;
    distanceKm: number;
    cumulativeKm: number;
    etaMinutes: number;
    isLastStop: boolean;
    invoiceUrl?: string | null;
    invoiceAmount?: number | null;
  }>;
}

export type DraftRouteStage = "orders" | "driver_vehicle" | "optimize";

/**
 * Transfer into a new route: Not delivered stops picked on a route's stop list and carried into
 * the Create route flow. Nothing moves until the new route is created.
 */
export interface RouteTransferContext {
  sourceRouteId: number;
  sourceRouteNumber: string;
  driverName: string;
  /** Active stops on the source route when the transfer started (to tell if it would be left empty). */
  sourceStopCount: number;
  /** Rows for the transferred stops, shown pinned on Create route even though they're not in the pool. */
  rows: RouteOptimizationOrderRow[];
  /** "Delete the empty route" checkbox, ticked by default. */
  deleteEmptySource: boolean;
}

/**
 * Unconfirmed route held per stage with a temporary frontend-only id ("draft-1", …).
 * Created/updated as the user progresses through create-route → driver/vehicle → optimize,
 * removed when the route is confirmed via the create API or discarded from View Routes.
 */
export interface DraftRoute {
  id: string;
  day: string;
  /** Last day the route runs (same as `day` for a single-day route). */
  endDay?: string | null;
  mode: "auto" | "manual";
  stage: DraftRouteStage;
  updatedAt: string;
  orders: RouteOptimizationOrderRow[];
  assignments: DriverVehicleAssignment[];
}

export interface RouteOptimizationState {
  /** Full rows for orders checked on Create Route Automatically (persists across pagination). */
  draftSelectedOrdersById: Record<string, RouteOptimizationOrderRow>;
  /** Unconfirmed draft routes keyed by temporary frontend id (survive tab changes / new sessions). */
  draftRoutesById: Record<string, DraftRoute>;
  /** Monotonic counter behind the temporary draft ids. */
  draftIdCounter: number;
  /** Draft the current create-route session belongs to; null until the first order is selected. */
  activeDraftId: string | null;
  /**
   * End date picked (with the start date) before choosing orders. Start date travels as the
   * orders' `deliveryDay`; null means not chosen yet (the create pop-up then asks).
   */
  routeEndDay: string | null;
  /** Set while creating a new route that receives stops transferred from an existing route. */
  transferContext: RouteTransferContext | null;
  /** Start / end point chosen for this route; null = default from Settings. */
  customRoutePoints: { start: RoutePoint | null; end: RoutePoint | null };
  selectedOrders: RouteOptimizationOrderRow[];
  selectedDriverId: string | null;
  selectedDriverName: string | null;
  selectedVehicleId: string | null;
  selectedVehicleLabel: string | null;
  /** Last fetched Google map route + stops (redux-persist). */
  googleMapRoute: GoogleMapRoutePersist | null;
  googleMapOptimizedStops: GoogleMapStopPersist[];
  /** Must match current orders+driver+vehicle fingerprint or stored map is ignored/cleared. */
  googleMapSnapshotKey: string | null;
  driverVehicleAssignmentsById: Record<string, DriverVehicleAssignment>;
  multiPreviewRoutes: MultiDriverPreviewRoute[];
  multiPreviewSnapshotKey: string | null;
  /** Last successful preview API result; shown again when `semanticPreviewKey` matches (e.g. user reverts split). */
  lastFetchedMultiPreview: { snapshotKey: string; routes: MultiDriverPreviewRoute[] } | null;
  activeMapViewDriverId: string | "master";
  manualSelectedDriverId: string | null;
  manualSelectedVehicleId: string | null;
  manualStops: Array<{
    stopSequence: number;
    orderNumber: number;
    C_Number: number;
    lat: number;
    lng: number;
    invoiceUrl?: string | null;
    invoiceAmount?: number | null;
  }>;
}

const initialState: RouteOptimizationState = {
  draftSelectedOrdersById: {},
  draftRoutesById: {},
  draftIdCounter: 0,
  activeDraftId: null,
  routeEndDay: null,
  transferContext: null,
  customRoutePoints: { start: null, end: null },
  selectedOrders: [],
  selectedDriverId: null,
  selectedDriverName: null,
  selectedVehicleId: null,
  selectedVehicleLabel: null,
  googleMapRoute: null,
  googleMapOptimizedStops: [],
  googleMapSnapshotKey: null,
  driverVehicleAssignmentsById: {},
  multiPreviewRoutes: [],
  multiPreviewSnapshotKey: null,
  lastFetchedMultiPreview: null,
  activeMapViewDriverId: "master",
  manualSelectedDriverId: null,
  manualSelectedVehicleId: null,
  manualStops: [],
};

const routeOptimizationSlice = createSlice({
  name: "routeOptimization",
  initialState,
  reducers: {
    /**
     * Snapshot the current create-route session into the active draft (creating one with a
     * temporary frontend id on first save). No-op when the session has no orders.
     */
    saveActiveDraft: (
      state,
      action: PayloadAction<{ day: string; mode: "auto" | "manual"; stage?: DraftRouteStage }>
    ) => {
      const orders =
        state.selectedOrders.length > 0 ? state.selectedOrders : Object.values(state.draftSelectedOrdersById);
      if (orders.length === 0) return;
      const assignments = Object.values(state.driverVehicleAssignmentsById);
      const requestedStage = action.payload.stage ?? "orders";
      // Never report a later stage than the data supports, nor lose driver/vehicle progress.
      const stage: DraftRouteStage =
        assignments.length === 0 ? "orders" : requestedStage === "orders" ? "driver_vehicle" : requestedStage;
      let id = state.activeDraftId;
      if (!id || !state.draftRoutesById[id]) {
        state.draftIdCounter += 1;
        id = `draft-${state.draftIdCounter}`;
        state.activeDraftId = id;
      }
      const day = action.payload.day || state.draftRoutesById[id]?.day || "";
      const endDay = state.routeEndDay ?? state.draftRoutesById[id]?.endDay ?? null;
      state.draftRoutesById[id] = {
        id,
        day,
        endDay,
        mode: action.payload.mode,
        stage,
        updatedAt: new Date().toISOString(),
        orders,
        assignments,
      };
    },
    /** Load a draft back into the session so the create-route wizard continues where it left off. */
    resumeDraftRoute: (state, action: PayloadAction<string>) => {
      const draft = state.draftRoutesById[action.payload];
      if (!draft) return;
      state.activeDraftId = draft.id;
      state.routeEndDay = draft.endDay ?? null;
      state.selectedOrders = draft.orders ?? [];
      const byId: Record<string, RouteOptimizationOrderRow> = {};
      for (const o of draft.orders ?? []) {
        if (o?.id) byId[o.id] = o;
      }
      state.draftSelectedOrdersById = byId;
      const assignmentsById: Record<string, DriverVehicleAssignment> = {};
      for (const a of draft.assignments ?? []) {
        if (a?.driverId != null) assignmentsById[String(a.driverId)] = a;
      }
      state.driverVehicleAssignmentsById = assignmentsById;
      if (draft.mode === "manual") {
        const first = draft.assignments?.[0];
        state.manualSelectedDriverId = first ? String(first.driverId) : null;
        state.manualSelectedVehicleId = first?.truckId != null && String(first.truckId) ? String(first.truckId) : null;
      } else {
        state.manualSelectedDriverId = null;
        state.manualSelectedVehicleId = null;
      }
      // Map previews are keyed to the previous session fingerprint — recompute after resume.
      state.googleMapRoute = null;
      state.googleMapOptimizedStops = [];
      state.googleMapSnapshotKey = null;
      state.multiPreviewRoutes = [];
      state.multiPreviewSnapshotKey = null;
      state.lastFetchedMultiPreview = null;
      state.activeMapViewDriverId = "master";
      state.manualStops = [];
    },
    discardDraftRoute: (state, action: PayloadAction<string>) => {
      delete state.draftRoutesById[action.payload];
      if (state.activeDraftId === action.payload) state.activeDraftId = null;
    },
    /**
     * Weekly cleanup: drafts only live until the end of the week they were saved in.
     * Removes every draft last updated before the current week's Sunday 00:00 (local),
     * so once a new week starts (Sunday) all of the previous week's drafts are dropped
     * from the persisted store on the next visit.
     */
    purgeDraftRoutesFromPastWeeks: (state) => {
      const now = new Date();
      // getDay(): 0 = Sunday, so this is Sunday 00:00 of the current week.
      const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      for (const id of Object.keys(state.draftRoutesById)) {
        const updatedAt = state.draftRoutesById[id]?.updatedAt;
        const updated = updatedAt ? new Date(updatedAt) : null;
        if (!updated || isNaN(updated.getTime()) || updated < weekStart) {
          delete state.draftRoutesById[id];
          if (state.activeDraftId === id) state.activeDraftId = null;
        }
      }
    },
    /** Route was confirmed via the create API — the active draft is no longer a draft. */
    confirmActiveDraft: (state) => {
      if (state.activeDraftId) delete state.draftRoutesById[state.activeDraftId];
      state.activeDraftId = null;
    },
    /** Start/end dates are chosen before orders; the end date is kept here for the whole wizard. */
    setRouteEndDay: (state, action: PayloadAction<string | null>) => {
      state.routeEndDay = action.payload ?? null;
    },
    /** Starts (or clears) a transfer into a new route; its stops come pre-ticked. */
    setTransferContext: (state, action: PayloadAction<RouteTransferContext | null>) => {
      state.transferContext = action.payload;
      if (action.payload) {
        const byId: Record<string, RouteOptimizationOrderRow> = { ...state.draftSelectedOrdersById };
        for (const row of action.payload.rows) byId[row.id] = row;
        state.draftSelectedOrdersById = byId;
      }
    },
    /** Pick a custom start or end point (null = back to the Settings default). */
    setCustomRoutePoint: (state, action: PayloadAction<{ which: "start" | "end"; point: RoutePoint | null }>) => {
      if (!state.customRoutePoints) state.customRoutePoints = { start: null, end: null };
      state.customRoutePoints[action.payload.which] = action.payload.point;
    },
    setTransferDeleteEmptySource: (state, action: PayloadAction<boolean>) => {
      if (state.transferContext) state.transferContext.deleteEmptySource = action.payload;
    },
    /** "Cancel transfer": drop the transfer and its stops from the selection; the source route is untouched. */
    cancelTransfer: (state) => {
      const ctx = state.transferContext;
      if (!ctx) return;
      const ids = new Set(ctx.rows.map((r) => r.id));
      const byId: Record<string, RouteOptimizationOrderRow> = {};
      for (const [id, row] of Object.entries(state.draftSelectedOrdersById)) if (!ids.has(id)) byId[id] = row;
      state.draftSelectedOrdersById = byId;
      state.selectedOrders = state.selectedOrders.filter((r) => !ids.has(r.id));
      state.transferContext = null;
    },
    /**
     * Optimize screen: add orders to the route being built without losing the driver / vehicle
     * picks or the manual stop order (new stops go to the end).
     */
    addOrdersToSession: (state, action: PayloadAction<RouteOptimizationOrderRow[]>) => {
      const existing = new Set(state.selectedOrders.map((o) => o.id));
      const added = (action.payload ?? []).filter((r) => !existing.has(r.id));
      if (!added.length) return;
      state.selectedOrders = [...state.selectedOrders, ...added];
      for (const r of added) state.draftSelectedOrdersById[r.id] = r;
      const base = state.manualStops.length;
      state.manualStops = [
        ...state.manualStops,
        ...added.map((r, i) => ({
          stopSequence: base + i + 1,
          orderNumber: Number(r.srNo),
          C_Number: Number(r.customerNumber ?? 0),
          lat: Number(r.customerLat ?? 0),
          lng: Number(r.customerLng ?? 0),
          invoiceUrl: r.invoiceUrl ?? null,
          invoiceAmount: r.invoiceAmount ?? null,
        })),
      ];
    },
    /** Optimize screen: take one order out of the route being built; the other stops close the gap. */
    removeOrderFromSession: (state, action: PayloadAction<string>) => {
      const row = state.selectedOrders.find((o) => o.id === action.payload);
      if (!row) return;
      state.selectedOrders = state.selectedOrders.filter((o) => o.id !== row.id);
      delete state.draftSelectedOrdersById[row.id];
      state.manualStops = state.manualStops
        .filter((s) => Number(s.orderNumber) !== Number(row.srNo))
        .map((s, i) => ({ ...s, stopSequence: i + 1 }));
    },
    setDraftSelectedOrdersById: (state, action: PayloadAction<Record<string, RouteOptimizationOrderRow>>) => {
      state.draftSelectedOrdersById = action.payload ?? {};
    },
    /** Refresh stored rows when a page loads (e.g. updated location flags) without dropping other pages' selections. */
    mergeDraftSelectedOrdersFromRows: (state, action: PayloadAction<RouteOptimizationOrderRow[]>) => {
      const rows = action.payload ?? [];
      for (const row of rows) {
        if (state.draftSelectedOrdersById[row.id]) {
          state.draftSelectedOrdersById[row.id] = row;
        }
      }
    },
    setRouteOptimizationOrders: (state, action: PayloadAction<RouteOptimizationOrderRow[]>) => {
      const rows = action.payload ?? [];
      const prev = state.selectedOrders;

      /** Same order IDs as last session → keep driver/vehicle picks when user returns from driver step to orders and clicks Optimize again. */
      const sameOrderIdSet = (() => {
        if (prev.length !== rows.length) return false;
        const a = [...prev].map((r) => r.id).sort();
        const b = [...rows].map((r) => r.id).sort();
        return a.length === b.length && a.every((id, i) => id === b[i]);
      })();

      state.selectedOrders = rows;
      /** Keep draft selections in sync so Create Route tables still show checkboxes after Back from driver/vehicle or optimize screens. */
      const draft: Record<string, RouteOptimizationOrderRow> = {};
      for (const r of rows) {
        draft[r.id] = r;
      }
      state.draftSelectedOrdersById = draft;

      if (!sameOrderIdSet) {
        state.googleMapRoute = null;
        state.googleMapOptimizedStops = [];
        state.googleMapSnapshotKey = null;
        state.multiPreviewRoutes = [];
        state.multiPreviewSnapshotKey = null;
        state.lastFetchedMultiPreview = null;
        state.activeMapViewDriverId = "master";
        state.driverVehicleAssignmentsById = {};
        state.manualSelectedDriverId = null;
        state.manualSelectedVehicleId = null;
        state.manualStops = [];
      }
      /** Same orders as before: keep map preview, polylines, driver/vehicle, splits — back/optimize without changes. */
    },
    setGoogleMapResult: (
      state,
      action: PayloadAction<{
        route: GoogleMapRoutePersist;
        optimizedStops: GoogleMapStopPersist[];
        snapshotKey: string;
      }>
    ) => {
      state.googleMapRoute = action.payload.route;
      state.googleMapOptimizedStops = action.payload.optimizedStops ?? [];
      state.googleMapSnapshotKey = action.payload.snapshotKey;
    },
    clearGoogleMapResult: (state) => {
      state.googleMapRoute = null;
      state.googleMapOptimizedStops = [];
      state.googleMapSnapshotKey = null;
    },
    upsertDriverVehicleAssignment: (
      state,
      action: PayloadAction<{
        driverId: string;
        driverName: string;
        truckId: string;
        truckLabel: string;
        vehicleDetail?: AssignedVehicleSnapshot | null;
      }>
    ) => {
      const { driverName, truckId, truckLabel, vehicleDetail } = action.payload;
      const driverId = String(action.payload.driverId);
      const prev = state.driverVehicleAssignmentsById[driverId];
      state.driverVehicleAssignmentsById[driverId] = {
        driverId,
        driverName,
        truckId,
        truckLabel,
        split: prev?.split,
        vehicleDetail: vehicleDetail !== undefined ? vehicleDetail : prev?.vehicleDetail,
      };
    },
    setDriverSplit: (
      state,
      action: PayloadAction<{ driverId: string; split: number | undefined }>
    ) => {
      const id = String(action.payload.driverId);
      let curr = state.driverVehicleAssignmentsById[id];
      if (!curr) {
        const key = Object.keys(state.driverVehicleAssignmentsById).find((k) => String(k) === id);
        if (!key) return;
        curr = state.driverVehicleAssignmentsById[key];
      }
      curr.split = action.payload.split;
    },
    removeDriverVehicleAssignment: (state, action: PayloadAction<string>) => {
      const id = String(action.payload);
      if (state.driverVehicleAssignmentsById[id]) {
        delete state.driverVehicleAssignmentsById[id];
        return;
      }
      const key = Object.keys(state.driverVehicleAssignmentsById).find((k) => String(k) === id);
      if (key) delete state.driverVehicleAssignmentsById[key];
    },
    clearDriverVehicleAssignments: (state) => {
      state.driverVehicleAssignmentsById = {};
    },
    setMultiPreviewRoutes: (
      state,
      action: PayloadAction<{
        routes: MultiDriverPreviewRoute[];
        snapshotKey: string;
      }>
    ) => {
      const routes = action.payload.routes ?? [];
      const snapshotKey = action.payload.snapshotKey;
      state.multiPreviewRoutes = routes;
      state.multiPreviewSnapshotKey = snapshotKey;
      state.lastFetchedMultiPreview = { snapshotKey, routes };
      state.activeMapViewDriverId = "master";
    },
    clearMultiPreviewRoutes: (state) => {
      state.multiPreviewRoutes = [];
      state.multiPreviewSnapshotKey = null;
      state.lastFetchedMultiPreview = null;
      state.activeMapViewDriverId = "master";
    },
    setActiveMapViewDriverId: (state, action: PayloadAction<string | "master">) => {
      state.activeMapViewDriverId = action.payload;
    },
    setManualSelectedDriverId: (state, action: PayloadAction<string | null>) => {
      state.manualSelectedDriverId = action.payload;
    },
    setManualSelectedVehicleId: (state, action: PayloadAction<string | null>) => {
      state.manualSelectedVehicleId = action.payload;
    },
    setManualStops: (
      state,
      action: PayloadAction<
        Array<{
          stopSequence: number;
          orderNumber: number;
          C_Number: number;
          lat: number;
          lng: number;
          invoiceUrl?: string | null;
          invoiceAmount?: number | null;
        }>
      >
    ) => {
      state.manualStops = action.payload ?? [];
    },
    clearManualRouteDraft: (state) => {
      state.manualSelectedDriverId = null;
      state.manualSelectedVehicleId = null;
      state.manualStops = [];
    },
    setRouteOptimizationDriver: (
      state,
      action: PayloadAction<{ id: string; name: string } | null>
    ) => {
      if (!action.payload) {
        state.selectedDriverId = null;
        state.selectedDriverName = null;
        state.selectedVehicleId = null;
        state.selectedVehicleLabel = null;
        return;
      }
      state.selectedDriverId = action.payload.id;
      state.selectedDriverName = action.payload.name;
      state.selectedVehicleId = null;
      state.selectedVehicleLabel = null;
    },
    setRouteOptimizationVehicle: (
      state,
      action: PayloadAction<{ id: string; label: string } | null>
    ) => {
      if (!action.payload) {
        state.selectedVehicleId = null;
        state.selectedVehicleLabel = null;
        return;
      }
      state.selectedVehicleId = action.payload.id;
      state.selectedVehicleLabel = action.payload.label;
    },
    clearRouteOptimizationSession: (state) => {
      /** Draft routes survive: only the live wizard session resets. Next session gets a new draft id. */
      state.activeDraftId = null;
      state.routeEndDay = null;
      state.transferContext = null;
      state.customRoutePoints = { start: null, end: null };
      state.draftSelectedOrdersById = {};
      state.selectedOrders = [];
      state.selectedDriverId = null;
      state.selectedDriverName = null;
      state.selectedVehicleId = null;
      state.selectedVehicleLabel = null;
      state.googleMapRoute = null;
      state.googleMapOptimizedStops = [];
      state.googleMapSnapshotKey = null;
      state.driverVehicleAssignmentsById = {};
      state.multiPreviewRoutes = [];
      state.multiPreviewSnapshotKey = null;
      state.lastFetchedMultiPreview = null;
      state.activeMapViewDriverId = "master";
      state.manualSelectedDriverId = null;
      state.manualSelectedVehicleId = null;
      state.manualStops = [];
    },
  },
});

export const {
  saveActiveDraft,
  resumeDraftRoute,
  discardDraftRoute,
  purgeDraftRoutesFromPastWeeks,
  confirmActiveDraft,
  setRouteEndDay,
  setTransferContext,
  setCustomRoutePoint,
  setTransferDeleteEmptySource,
  cancelTransfer,
  setDraftSelectedOrdersById,
  addOrdersToSession,
  removeOrderFromSession,
  mergeDraftSelectedOrdersFromRows,
  setRouteOptimizationOrders,
  setGoogleMapResult,
  clearGoogleMapResult,
  upsertDriverVehicleAssignment,
  setDriverSplit,
  removeDriverVehicleAssignment,
  clearDriverVehicleAssignments,
  setMultiPreviewRoutes,
  clearMultiPreviewRoutes,
  setActiveMapViewDriverId,
  setManualSelectedDriverId,
  setManualSelectedVehicleId,
  setManualStops,
  clearManualRouteDraft,
  setRouteOptimizationDriver,
  setRouteOptimizationVehicle,
  clearRouteOptimizationSession,
} = routeOptimizationSlice.actions;

export default routeOptimizationSlice.reducer;
